/**
 * Connections page — provider auth status per user.
 *
 * v1 surface: shows Anthropic / OpenAI / Google AI / OpenRouter rows
 * with connected/not-connected status driven by the user's credentials
 * endpoint (any instance with a saved key counts as connected).
 *
 * "Connect" routes through the existing per-instance `authenticate_provider`
 * flow: user picks an instance to authenticate from. The fully-generalized
 * user-scope OAuth flow lands when the new bundle endpoint
 * (POST /credentials/:provider/bundle) is wired into a UI in Phase E.
 */

import { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { API_URL } from '../services/api';
import { useApiHeaders } from '../hooks/useApiHeaders';

interface ProviderRow {
  provider: 'anthropic' | 'openai' | 'google_ai' | 'openrouter';
  label: string;
  description: string;
  connected: boolean;
  accountEmail?: string;
  lastVerified?: number;
}

const PROVIDERS: Pick<ProviderRow, 'provider' | 'label' | 'description'>[] = [
  { provider: 'anthropic',   label: 'Anthropic',          description: 'Claude models for chat, code, and tools.' },
  { provider: 'openai',      label: 'OpenAI',             description: 'GPT family for chat and code.' },
  { provider: 'google_ai',   label: 'Google AI',          description: 'Gemini models.' },
  { provider: 'openrouter',  label: 'OpenRouter',         description: 'Multi-model gateway.' },
];

export default function ConnectionsPage() {
  const { getHeaders } = useApiHeaders();
  const [rows, setRows] = useState<ProviderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const headers = await getHeaders();
        // Aggregate across all user instances. Cheapest path that
        // doesn't require a dedicated /connections endpoint yet.
        const res = await fetch(`${API_URL}/api/v1/instances`, { headers });
        if (!res.ok) throw new Error(`Instances fetch failed: ${res.status}`);
        const data = await res.json() as { instances?: { instance_id: string }[] };
        const instances = data.instances ?? [];

        const seen = new Map<ProviderRow['provider'], ProviderRow>();
        for (const p of PROVIDERS) {
          seen.set(p.provider, { ...p, connected: false });
        }

        // Walk instances in parallel, collapse into per-provider status.
        await Promise.all(instances.map(async (inst) => {
          const h = await getHeaders();
          const r = await fetch(`${API_URL}/api/v1/credentials/${inst.instance_id}`, { headers: h });
          if (!r.ok) return;
          const body = await r.json() as { credentials?: Record<string, { is_configured: boolean; last_verified?: number }> };
          for (const [provider, status] of Object.entries(body.credentials ?? {})) {
            const row = seen.get(provider as ProviderRow['provider']);
            if (!row) continue;
            if (status.is_configured) row.connected = true;
            if (status.last_verified && (!row.lastVerified || status.last_verified > row.lastVerified)) {
              row.lastVerified = status.last_verified;
            }
          }
        }));

        setRows(Array.from(seen.values()));
      } catch (e: any) {
        setError(String(e?.message ?? e));
      } finally {
        setLoading(false);
      }
    })();
  }, [getHeaders]);

  return (
    <Layout title="Connections" subtitle="AI provider credentials shared across your workspaces">
      <div className="p-6 max-w-3xl mx-auto">

        {error && (
          <div className="mb-4 p-3 rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-sm text-slate-400 dark:text-slate-600">Loading…</div>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => (
              <div key={r.provider} className="flex items-center justify-between p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                <div>
                  <div className="flex items-center space-x-2">
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{r.label}</h3>
                    {r.connected ? (
                      <span className="px-2 py-0.5 text-[10px] font-medium rounded-md bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400">
                        Connected
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-[10px] font-medium rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                        Not connected
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{r.description}</p>
                  {r.lastVerified && (
                    <p className="text-[10px] text-slate-400 dark:text-slate-600 mt-1">
                      Last verified {new Date(r.lastVerified).toLocaleString()}
                    </p>
                  )}
                </div>
                <div className="text-xs text-slate-400 dark:text-slate-500">
                  {/* Connect/Disconnect lands in Phase E when the
                      user-scope OAuth bundle endpoint has a dedicated UI.
                      For now, per-instance Connect lives in InstanceConfig. */}
                  Manage per-instance →
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
